import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedListWidget } from '../../dashboard-basic-view/components/RankedListWidget/ranked-list-widget';
import { NoDataWidget } from '../../dashboard-basic-view/components/NoDataWidget/no-data-widget';
import { formatNumber } from '../../../reporting/components/portfolio-report';
import { ReportingPaths } from '../../../reporting/reporting.router';
import { PortfolioWidgetProps } from './portfolio-widget.types';

export function PortfolioTotalInterestBearingDebtWidget({ data, isLoading }: PortfolioWidgetProps) {
  const { t } = useTranslation('widget');
  const currencyCode = data?.currency ?? 'NOK';

  const { total, highestShare, lowestShare } = useMemo(() => {
    const withDebt = (data?.buildings ?? []).filter((b) => b.interestBearingDebt != null);

    // Headline: total interest-bearing debt; lists: each building's share (andel) of that total.
    const total = withDebt.reduce((sum, b) => sum + (b.interestBearingDebt as number), 0);

    const shares =
      total > 0
        ? withDebt.map((b) => ({
            id: b.id,
            label: b.name,
            value: Math.round(((b.interestBearingDebt as number) / total) * 1000) / 10,
          }))
        : [];

    return {
      total,
      highestShare: [...shares].sort((a, b) => b.value - a.value).slice(0, 3),
      lowestShare: [...shares].sort((a, b) => a.value - b.value).slice(0, 3),
    };
  }, [data]);

  if (!isLoading && highestShare.length === 0 && lowestShare.length === 0)
    return (
      <NoDataWidget
        title={t('portfolioTotalInterestBearingDebt.title')}
        noDataText={t('portfolioTotalInterestBearingDebt.noData')}
      />
    );

  return (
    <RankedListWidget
      to={ReportingPaths.FinancialOverview}
      title={t('portfolioTotalInterestBearingDebt.title')}
      tooltip={t('portfolioTotalInterestBearingDebt.tooltip')}
      value={formatNumber(total / 1000)}
      unit={`k${currencyCode}`}
      rankUnit="%"
      loading={isLoading || !data}
      highListTitle={t('portfolioTotalInterestBearingDebt.highestShare')}
      lowListTitle={t('portfolioTotalInterestBearingDebt.lowestShare')}
      highListData={highestShare}
      lowListData={lowestShare}
    />
  );
}
